import React from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';import { AuthProvider, useAuth } from './AuthContext';
import { SubscriptionProvider, ModuleGate } from './SubscriptionContext';
import { TestContextProvider } from './TestContext';
import { ProtectedRoute } from './shell/ProtectedRoute';
import AppShell from './shell/AppShell';
import LoginPage from './pages/LoginPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import TokenPrintView from './pages/TokenPrintView';
import QueueTVPage from './pages/QueueTVPage';
// ────────────────────────────────────────────────────────────────
// LANDING PAGE — ROLLBACK NOTE (2026-07-26)
// Active landing is `LandingPageV3` ("Modern Clinical OS" — bone + saffron).
// Previous v2 landing is preserved at `./modules/landing/v2/LandingPage`
// and reachable at `/legacy-landing` for side-by-side compare.
// To roll back in one line: change the import below to
//   `import LandingPage from './modules/landing/v2/LandingPage';`
// (No other change needed. The v2 tree is untouched.)
// ────────────────────────────────────────────────────────────────
import LandingPage from './modules/landing/LandingPageV3';
import LegacyLandingPage from './modules/landing/v2/LandingPage';
import DemoPage from './modules/demo/DemoPage';
import StoriesDemo from './modules/demo/StoriesDemo';
import SignupPage from './modules/landing/SignupPage';
import VerifyEmailPage from './modules/landing/VerifyEmailPage';
import BillingModule from './modules/billing/BillingModule';
import AccountsRevenuePage from './modules/accounts/AccountsRevenuePage';
import CompliancePolicyPack from './modules/compliance/CompliancePolicyPack';
import TestProceduresModule from './modules/test/TestProceduresModule';
import AudiogramReportPage from './pages/AudiogramReportPage';
import HAModule from './modules/ha/HAModule';
import RepairModule from './modules/repair/RepairModule';
import AdminPanel from './modules/admin/panel/AdminPanel';
import SettingsModule from './modules/settings/SettingsModule';
import ClinicalAnalyticsPage from './modules/admin/ClinicalAnalyticsPage';
import ReferralPartnersPage from './modules/admin/ReferralPartnersPage';
import ReferralCornerPage from './modules/referrals/ReferralCornerPage';
import PartnerPortalPage from './modules/partner/PartnerPortalPage';
import PatientPortal from './modules/patient/PatientPortal';
import DataExportPage from './modules/data/DataExportPage';
import { NoPermission } from './shell/NoPermission';
import ReportsModule from './modules/reports/ReportsModule';
import AppointmentsCalendarPage from './modules/appointments/AppointmentsCalendarPage';
import { usePageViewTracker } from './hooks/usePageViewTracker';
import { useMarketingTracker } from './utils/marketingTracker';
import { useDocumentTitle } from './hooks/useDocumentTitle';
import TopProgressBar from './components/TopProgressBar';
import TierBadgeWidget from './components/TierBadgeWidget';
import AgentNexaWidget from './modules/help/AgentNexaWidget';
import { ConnectivityProvider } from './connectivity/ConnectivityContext';
import InviteAcceptPage from './modules/auth/InviteAcceptPage';
import AudinexaCarePage from './modules/care/AudinexaCarePage';
import ReferEarnPage from './modules/referral/ReferEarnPage';
import ReferralTermsPage from './modules/referral/ReferralTermsPage';
import LegalPage from './modules/legal/LegalPage';
import PatientsModule from './modules/patients/PatientsModule';
import CloseoutPage from './modules/closeout/CloseoutPage';
import EodReportPage from './modules/reports/EodReportPage';
import StatusPage from './pages/StatusPage';
import DemoShowcasePage from './modules/demo/showcase/DemoShowcasePage';
import BrandKitPage from './modules/brandkit/BrandKitPage';
import PublicBrandKitPage from './modules/brandkit/PublicBrandKitPage';

// Post-login redirect by role
const INTERNAL_ADMIN_ROLES = ['founder', 'super_admin', 'sales_manager', 'support_agent', 'finance_manager', 'product_ops', 'read_only'];
const PostLoginRedirect = () => {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (INTERNAL_ADMIN_ROLES.includes(user.role)) return <Navigate to="/admin/dashboard" replace />;
  if (user.role === 'referral_partner') return <Navigate to="/partner" replace />;
  if (user.role === 'audiologist') return <Navigate to="/test" replace />;
  return <Navigate to="/patients" replace />;
};

// Data Export: owner / accounts / platform roles only (backend also 403s others).
const DataExportGate = () => {
  const { user } = useAuth();
  return ['clinic_owner', 'accounts', 'super_admin', 'founder'].includes(user?.role) ? <DataExportPage /> : <NoPermission />;
};

const ShelledRoute = ({ children }) => {
  return (
    <ProtectedRoute>
      <PartnerRedirect>
        <AppShell>{children}</AppShell>
      </PartnerRedirect>
    </ProtectedRoute>
  );
};

const PartnerRedirect = ({ children }) => {
  const { user } = useAuth();
  if (user?.role === 'referral_partner') return <Navigate to="/partner" replace />;
  return children;
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <SubscriptionProvider>
          <ConnectivityProvider>
            <TestContextProvider>
              <AppRoutes />
            </TestContextProvider>
          </ConnectivityProvider>
        </SubscriptionProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

function AppRoutes() {
  // Track page views on every route change (authenticated users only)
  usePageViewTracker();
  useMarketingTracker();
  // Update browser tab title
  useDocumentTitle();
  return (
    <>
      <TopProgressBar />
      <Routes>
              {/* PUBLIC */}
              <Route path="/" element={<LandingPage />} />
              <Route path="/legacy-landing" element={<LegacyLandingPage />} />
              <Route path="/demo" element={<StoriesDemo />} />
              <Route path="/demo/features" element={<DemoPage />} />
              <Route path="/signup" element={<SignupPage />} />
              <Route path="/verify-email" element={<VerifyEmailPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
              <Route path="/invite/:token" element={<InviteAcceptPage />} />
              <Route path="/queue/:clinicId" element={<QueueTVPage />} />
              <Route path="/patient-portal" element={<PatientPortal />} />
              <Route path="/patient-portal/:clinicId" element={<PatientPortal />} />
              <Route path="/status" element={<StatusPage />} />
              <Route path="/brand/:token" element={<PublicBrandKitPage />} />

              {/* PUBLIC LEGAL — required for Razorpay / payment gateway KYC */}
              <Route path="/terms" element={<LegalPage />} />
              <Route path="/referral-terms" element={<ReferralTermsPage />} />
              <Route path="/privacy" element={<LegalPage />} />
              <Route path="/refund" element={<LegalPage />} />
              <Route path="/contact" element={<LegalPage />} />

              {/* AUTHENTICATED */}
              <Route path="/app" element={<PostLoginRedirect />} />
              <Route path="/token/:tokenId" element={<ProtectedRoute><TokenPrintView /></ProtectedRoute>} />

              {/* PARTNER (own shell — no AppShell) */}
              <Route path="/partner" element={<ProtectedRoute><PartnerPortalPage /></ProtectedRoute>} />

              <Route path="/frontdesk/*" element={<Navigate to="/patients" replace />} />
              <Route path="/closeout" element={
                <ShelledRoute><CloseoutPage /></ShelledRoute>
              } />
              <Route path="/patients/*" element={
                <ShelledRoute><PatientsModule /></ShelledRoute>
              } />
              <Route path="/appointments" element={
                <ShelledRoute><AppointmentsCalendarPage /></ShelledRoute>
              } />
              <Route path="/billing/*" element={<ShelledRoute><BillingModule /></ShelledRoute>} />
              <Route path="/accounts" element={
                <ShelledRoute><AccountsRevenuePage /></ShelledRoute>
              } />
              <Route path="/settings/compliance" element={
                <ShelledRoute><CompliancePolicyPack /></ShelledRoute>
              } />
              <Route path="/test/audiogram/:sessionId" element={
                <ShelledRoute><AudiogramReportPage /></ShelledRoute>
              } />
              <Route path="/test/*" element={
                <ShelledRoute><ModuleGate module="diagnostics"><TestProceduresModule /></ModuleGate></ShelledRoute>
              } />
              <Route path="/reports" element={
                <ShelledRoute><ReportsModule /></ShelledRoute>
              } />
              <Route path="/reports/eod" element={
                <ShelledRoute><EodReportPage /></ShelledRoute>
              } />
              <Route path="/ha/*" element={
                <ShelledRoute><ModuleGate module="hearing-aids"><HAModule /></ModuleGate></ShelledRoute>
              } />
              <Route path="/repair/*" element={
                <ShelledRoute><ModuleGate module="repair"><RepairModule /></ModuleGate></ShelledRoute>
              } />

              {/* Clinical Analytics + Referral Partners (PREMIUM) */}
              <Route path="/analytics/clinical" element={
                <ShelledRoute><ModuleGate module="analytics"><ClinicalAnalyticsPage /></ModuleGate></ShelledRoute>
              } />
              <Route path="/partners" element={
                <ShelledRoute><ModuleGate module="referral-partners"><ReferralPartnersPage /></ModuleGate></ShelledRoute>
              } />

              {/* Referral Corner — owner-grade payout dashboard (access enforced inside the page) */}
              <Route path="/referrals" element={
                <ShelledRoute><ReferralCornerPage /></ShelledRoute>
              } />

              {/* CLINIC DATA EXPORT — role-gated inside the page */}
              <Route path="/data-export" element={
                <ShelledRoute><DataExportGate /></ShelledRoute>
              } />
              <Route path="/settings/*" element={
                <ShelledRoute><SettingsModule /></ShelledRoute>
              } />
              <Route path="/care" element={
                <ShelledRoute><AudinexaCarePage /></ShelledRoute>
              } />
              <Route path="/refer" element={
                <ShelledRoute><ReferEarnPage /></ShelledRoute>
              } />

              {/* SUPER-ADMIN PANEL (founder / super_admin only — own shell) */}
              <Route path="/admin/*" element={<ProtectedRoute><AdminPanel /></ProtectedRoute>} />

              {/* Founder-only product showcase (Hear All demo clinic) */}
              <Route path="/demo-showcase" element={<ProtectedRoute><DemoShowcasePage /></ProtectedRoute>} />
              <Route path="/brand-kit" element={<ProtectedRoute><BrandKitPage /></ProtectedRoute>} />

              {/* Founder bookmark alias — /founder/health-check → /admin/health-check */}
              <Route path="/founder/health-check" element={<Navigate to="/admin/health-check" replace />} />

              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
      <TierBadgeWidget />
      <AgentNexaWidget />
    </>
  );
}

export default App;
